/**
 * Learnbee Dialogue — database helpers.
 *
 * Mirrors src/lib/db.ts patterns but simpler (no localStorage fallback —
 * Dialogue has no legacy data). Errors are thrown loudly so RLS / schema
 * mismatches surface immediately in the console.
 */

import { customAlphabet } from 'nanoid'
import { supabase } from './supabase'
import type {
  DialogueConfig,
  DialogueKbFile,
  DialogueCustomerProfile,
  DialogueScenario,
  DialogueSession,
  ScenarioSource,
  VoiceGender,
} from '@/types/dialogue'

// Share-code alphabet: same as published-courses (digits + uppercase,
// ambiguous chars removed: 0 O 1 I L). Re-used so admins / learners aren't
// confused by inconsistent code formats across Learnbee + Dialogue.
const SHARE_CODE_ALPHABET = '23456789ABCDEFGHJKLMNPQRSTUVWXYZ'
const generateShareCode = customAlphabet(SHARE_CODE_ALPHABET, 6)

/**
 * Placeholder rubric seeded on draft creation. The wizard's Rubric step
 * is expected to overwrite it before publish.
 */
const RUBRIC_PLACEHOLDER =
  'Evaluate how well the learner demonstrates understanding of the subject. ' +
  'Consider accuracy, completeness, and the ability to apply knowledge to the scenario.'

// ─── Reads ────────────────────────────────────────────────────────────────────

/**
 * List all Dialogues owned by the current user.
 * RLS filters by created_by automatically via the Clerk JWT.
 */
export async function getDialogues(): Promise<DialogueConfig[]> {
  const { data, error } = await supabase
    .from('dialogue_configs')
    .select('*')
    .order('created_at', { ascending: false })

  if (error) throw error
  return (data ?? []) as DialogueConfig[]
}

export async function getDialogue(id: string): Promise<DialogueConfig | null> {
  const { data, error } = await supabase
    .from('dialogue_configs')
    .select('*')
    .eq('id', id)
    .maybeSingle()

  if (error) throw error
  return (data ?? null) as DialogueConfig | null
}

// ─── Writes ───────────────────────────────────────────────────────────────────

export async function createDialogue(opts: {
  createdBy: string
  title?: string
  coachName?: string
  language?: string
  voiceGender?: VoiceGender
}): Promise<DialogueConfig> {
  // id is gen_random_uuid() at the DB level — never set client-side.
  // created_at / updated_at also default at the DB level.
  const { data, error } = await supabase
    .from('dialogue_configs')
    .insert({
      code: generateShareCode(),
      created_by: opts.createdBy,
      org_id: null,                  // populated later when org roles land (see Course pattern)
      title: opts.title ?? 'Untitled Role Play',
      coach_name: opts.coachName ?? 'Coach',
      language: opts.language ?? 'en',
      voice_gender: opts.voiceGender ?? 'F',
      // coaching_tone, mode (= 'customer_led'), scenario_order default at DB level
      rubric: RUBRIC_PLACEHOLDER,
      // Customer-persona defaults — pick opposite voice for vocal contrast.
      customer_name: 'Customer',
      customer_voice_gender: (opts.voiceGender ?? 'F') === 'F' ? 'M' : 'F',
      customer_persona: '',
    })
    .select()
    .single()

  if (error) throw error
  return data as DialogueConfig
}

/**
 * Partial update. `updated_at` is bumped automatically.
 * Cannot patch id / created_by / created_at.
 */
export async function updateDialogue(
  id: string,
  patch: Partial<Omit<DialogueConfig, 'id' | 'created_by' | 'created_at' | 'updated_at'>>,
): Promise<void> {
  const { error } = await supabase
    .from('dialogue_configs')
    .update({ ...patch, updated_at: new Date().toISOString() })
    .eq('id', id)

  if (error) throw error
}

/**
 * Delete the dialogue. ON DELETE CASCADE on the foreign keys cleans up
 * dialogue_kb_files, dialogue_scenarios, and dialogue_sessions automatically.
 */
export async function deleteDialogue(id: string): Promise<void> {
  const { error } = await supabase
    .from('dialogue_configs')
    .delete()
    .eq('id', id)

  if (error) throw error
}

// ─── Coach icons ──────────────────────────────────────────────────────────────
//
// Curated emoji set the admin picks from. The chosen emoji is stored verbatim
// in dialogue_configs.icon_url. The column also still accepts http(s) URLs in
// case we add custom uploads later — the renderer disambiguates via
// isImageUrl().

/**
 * Curated emoji set used by BOTH the coach and customer-profile icon
 * pickers (the name `COACH_ICONS` is historical; rename pending a
 * bigger refactor). The set is hand-picked for character-avatar use:
 * people in different roles, friendly animals, faces with distinct
 * moods, and a few fantasy / symbol options for personality. ~100
 * entries so the picker feels Notion-like — broad enough to be
 * delightful, narrow enough to keep load + scroll fast.
 *
 * Each entry has a searchable `label` and optional `keywords` for
 * the picker's search box (so "happy" finds the grin emoji even
 * though its label is "Grinning face").
 */
export interface CoachIconOption {
  id: string
  emoji: string
  label: string
  keywords?: string
}

export const COACH_ICONS: readonly CoachIconOption[] = [
  // ── People — generic ──
  { id: 'person',      emoji: '🧑',    label: 'Person',          keywords: 'human adult' },
  { id: 'woman',       emoji: '👩',    label: 'Woman' },
  { id: 'man',         emoji: '👨',    label: 'Man' },
  { id: 'girl',        emoji: '👧',    label: 'Girl',            keywords: 'young kid' },
  { id: 'boy',         emoji: '👦',    label: 'Boy',             keywords: 'young kid' },
  { id: 'baby',        emoji: '👶',    label: 'Baby',            keywords: 'infant' },
  { id: 'old-woman',   emoji: '👵',    label: 'Elderly woman',   keywords: 'senior grandma' },
  { id: 'old-man',     emoji: '👴',    label: 'Elderly man',     keywords: 'senior grandpa' },
  { id: 'curly-woman', emoji: '👩‍🦱', label: 'Woman, curly hair' },
  { id: 'curly-man',   emoji: '👨‍🦱', label: 'Man, curly hair' },
  { id: 'bald-woman',  emoji: '👩‍🦲', label: 'Woman, bald' },
  { id: 'bald-man',    emoji: '👨‍🦲', label: 'Man, bald' },
  { id: 'red-woman',   emoji: '👩‍🦰', label: 'Woman, red hair' },
  { id: 'red-man',     emoji: '👨‍🦰', label: 'Man, red hair' },
  { id: 'white-woman', emoji: '👩‍🦳', label: 'Woman, white hair' },
  { id: 'white-man',   emoji: '👨‍🦳', label: 'Man, white hair' },

  // ── People — roles ──
  { id: 'teacher',     emoji: '🧑‍🏫', label: 'Teacher' },
  { id: 'teacher-f',   emoji: '👩‍🏫', label: 'Teacher (F)' },
  { id: 'teacher-m',   emoji: '👨‍🏫', label: 'Teacher (M)' },
  { id: 'office',      emoji: '🧑‍💼', label: 'Office worker',   keywords: 'business suit' },
  { id: 'office-f',    emoji: '👩‍💼', label: 'Office (F)',      keywords: 'business suit' },
  { id: 'office-m',    emoji: '👨‍💼', label: 'Office (M)',      keywords: 'business suit' },
  { id: 'scientist',   emoji: '🧑‍🔬', label: 'Scientist',       keywords: 'lab research' },
  { id: 'developer',   emoji: '🧑‍💻', label: 'Developer',       keywords: 'coder programmer tech' },
  { id: 'health',      emoji: '🧑‍⚕️', label: 'Health worker',   keywords: 'doctor nurse medical' },
  { id: 'chef',        emoji: '🧑‍🍳', label: 'Chef',            keywords: 'cook' },
  { id: 'artist',      emoji: '🧑‍🎨', label: 'Artist',          keywords: 'painter creative' },
  { id: 'farmer',      emoji: '🧑‍🌾', label: 'Farmer' },
  { id: 'mechanic',    emoji: '🧑‍🔧', label: 'Mechanic',        keywords: 'engineer wrench' },
  { id: 'factory',     emoji: '🧑‍🏭', label: 'Factory worker' },
  { id: 'judge',       emoji: '🧑‍⚖️', label: 'Judge',           keywords: 'lawyer' },
  { id: 'pilot',       emoji: '🧑‍✈️', label: 'Pilot' },
  { id: 'astronaut',   emoji: '🧑‍🚀', label: 'Astronaut',       keywords: 'space' },
  { id: 'firefighter', emoji: '🧑‍🚒', label: 'Firefighter' },
  { id: 'police',      emoji: '👮',    label: 'Police officer' },
  { id: 'detective',   emoji: '🕵️',   label: 'Detective',       keywords: 'spy' },
  { id: 'guard',       emoji: '💂',    label: 'Guard' },
  { id: 'construction',emoji: '👷',    label: 'Construction',    keywords: 'builder hardhat' },
  { id: 'ninja',       emoji: '🥷',    label: 'Ninja' },
  { id: 'superhero',   emoji: '🦸',    label: 'Superhero',       keywords: 'hero' },
  { id: 'supervillain',emoji: '🦹',    label: 'Supervillain',    keywords: 'villain' },
  { id: 'mage',        emoji: '🧙',    label: 'Mage',            keywords: 'wizard magic' },
  { id: 'fairy',       emoji: '🧚',    label: 'Fairy' },
  { id: 'vampire',     emoji: '🧛',    label: 'Vampire' },
  { id: 'zombie',      emoji: '🧟',    label: 'Zombie' },
  { id: 'elf',         emoji: '🧝',    label: 'Elf' },
  { id: 'genie',       emoji: '🧞',    label: 'Genie' },
  { id: 'merperson',   emoji: '🧜',    label: 'Mermaid',         keywords: 'merperson merman' },
  { id: 'cowboy',      emoji: '🤠',    label: 'Cowboy',          keywords: 'cowgirl hat' },

  // ── Faces & moods ──
  { id: 'grin',        emoji: '😀',    label: 'Grinning',        keywords: 'happy smile' },
  { id: 'joy',         emoji: '😂',    label: 'Tears of joy',    keywords: 'laughing lol' },
  { id: 'rofl',        emoji: '🤣',    label: 'Rolling laughing',keywords: 'rofl funny' },
  { id: 'smile',       emoji: '🙂',    label: 'Slight smile' },
  { id: 'wink',        emoji: '😉',    label: 'Winking' },
  { id: 'heart-eyes',  emoji: '😍',    label: 'Heart eyes',      keywords: 'love smitten' },
  { id: 'star-eyes',   emoji: '🤩',    label: 'Star eyes',       keywords: 'amazed wow' },
  { id: 'kiss',        emoji: '😘',    label: 'Blowing kiss' },
  { id: 'tongue',      emoji: '😋',    label: 'Yummy',           keywords: 'tongue tasty' },
  { id: 'sunglasses',  emoji: '😎',    label: 'Cool',            keywords: 'sunglasses confident' },
  { id: 'nerd',        emoji: '🤓',    label: 'Nerd',            keywords: 'smart glasses' },
  { id: 'monocle',     emoji: '🧐',    label: 'Monocle',         keywords: 'curious sceptical' },
  { id: 'thinking',    emoji: '🤔',    label: 'Thinking',        keywords: 'hmm pondering' },
  { id: 'neutral',     emoji: '😐',    label: 'Neutral' },
  { id: 'expressionless',emoji: '😑',  label: 'Expressionless',  keywords: 'meh' },
  { id: 'unamused',    emoji: '😒',    label: 'Unamused',        keywords: 'annoyed sceptical' },
  { id: 'raised-brow', emoji: '🤨',    label: 'Raised eyebrow',  keywords: 'sceptical doubt' },
  { id: 'smirk',       emoji: '😏',    label: 'Smirking' },
  { id: 'hush',        emoji: '🤫',    label: 'Shushing',        keywords: 'quiet secret' },
  { id: 'mind-blown',  emoji: '🤯',    label: 'Mind blown',      keywords: 'shocked exploding' },
  { id: 'partying',    emoji: '🥳',    label: 'Partying',        keywords: 'celebration birthday' },
  { id: 'pleading',    emoji: '🥺',    label: 'Pleading',        keywords: 'begging puppy eyes' },
  { id: 'frustrated',  emoji: '😤',    label: 'Frustrated',      keywords: 'angry steam' },
  { id: 'angry',       emoji: '😠',    label: 'Angry',           keywords: 'mad' },
  { id: 'rage',        emoji: '😡',    label: 'Rage',            keywords: 'furious red' },
  { id: 'crying',      emoji: '😢',    label: 'Crying',          keywords: 'sad tear' },
  { id: 'sob',         emoji: '😭',    label: 'Sobbing',         keywords: 'very sad' },
  { id: 'worried',     emoji: '😟',    label: 'Worried',         keywords: 'concerned' },
  { id: 'fearful',     emoji: '😨',    label: 'Fearful',         keywords: 'scared' },
  { id: 'cold-sweat',  emoji: '😰',    label: 'Cold sweat',      keywords: 'anxious nervous' },
  { id: 'sleepy',      emoji: '😴',    label: 'Sleepy',          keywords: 'sleeping zzz' },
  { id: 'dizzy',       emoji: '😵',    label: 'Dizzy',           keywords: 'confused' },
  { id: 'money',       emoji: '🤑',    label: 'Money face',      keywords: 'rich dollar' },
  { id: 'hot',         emoji: '🥵',    label: 'Hot face',        keywords: 'overheated' },
  { id: 'cold',        emoji: '🥶',    label: 'Cold face',       keywords: 'freezing' },
  { id: 'sick',        emoji: '🤒',    label: 'Sick' },

  // ── Animals — friendly ──
  { id: 'dog',         emoji: '🐶',    label: 'Dog',             keywords: 'puppy' },
  { id: 'cat',         emoji: '🐱',    label: 'Cat',             keywords: 'kitten' },
  { id: 'mouse',       emoji: '🐭',    label: 'Mouse' },
  { id: 'hamster',     emoji: '🐹',    label: 'Hamster' },
  { id: 'rabbit',      emoji: '🐰',    label: 'Rabbit',          keywords: 'bunny' },
  { id: 'fox',         emoji: '🦊',    label: 'Fox' },
  { id: 'bear',        emoji: '🐻',    label: 'Bear' },
  { id: 'panda',       emoji: '🐼',    label: 'Panda' },
  { id: 'koala',       emoji: '🐨',    label: 'Koala' },
  { id: 'tiger',       emoji: '🐯',    label: 'Tiger' },
  { id: 'lion',        emoji: '🦁',    label: 'Lion' },
  { id: 'monkey',      emoji: '🐵',    label: 'Monkey' },
  { id: 'frog',        emoji: '🐸',    label: 'Frog' },
  { id: 'penguin',     emoji: '🐧',    label: 'Penguin' },
  { id: 'chicken',     emoji: '🐔',    label: 'Chicken' },
  { id: 'owl',         emoji: '🦉',    label: 'Owl',             keywords: 'wise night' },
  { id: 'eagle',       emoji: '🦅',    label: 'Eagle' },
  { id: 'unicorn',     emoji: '🦄',    label: 'Unicorn' },
  { id: 'dragon',      emoji: '🐉',    label: 'Dragon' },
  { id: 'whale',       emoji: '🐳',    label: 'Whale' },
  { id: 'dolphin',     emoji: '🐬',    label: 'Dolphin' },
  { id: 'octopus',     emoji: '🐙',    label: 'Octopus' },
  { id: 'butterfly',   emoji: '🦋',    label: 'Butterfly' },
  { id: 'bee',         emoji: '🐝',    label: 'Bee' },
  { id: 'ladybug',     emoji: '🐞',    label: 'Ladybug' },

  // ── Symbols & objects ──
  { id: 'robot',       emoji: '🤖',    label: 'Robot' },
  { id: 'alien',       emoji: '👽',    label: 'Alien' },
  { id: 'ghost',       emoji: '👻',    label: 'Ghost' },
  { id: 'skull',       emoji: '💀',    label: 'Skull' },
  { id: 'brain',       emoji: '🧠',    label: 'Brain',           keywords: 'smart think' },
  { id: 'sparkles',    emoji: '✨',    label: 'Sparkles',        keywords: 'magic stars' },
  { id: 'fire',        emoji: '🔥',    label: 'Fire',            keywords: 'hot lit' },
  { id: 'lightning',   emoji: '⚡',    label: 'Lightning',       keywords: 'fast energy' },
  { id: 'rocket',      emoji: '🚀',    label: 'Rocket',          keywords: 'launch' },
  { id: 'star',        emoji: '⭐',    label: 'Star' },
  { id: 'crown',       emoji: '👑',    label: 'Crown',           keywords: 'royal king queen' },
  { id: 'gem',         emoji: '💎',    label: 'Gem',             keywords: 'diamond' },
  { id: 'trophy',      emoji: '🏆',    label: 'Trophy',          keywords: 'winner award' },
  { id: 'party',       emoji: '🎉',    label: 'Party popper',    keywords: 'celebration' },
  { id: 'balloon',     emoji: '🎈',    label: 'Balloon' },
  { id: 'rose',        emoji: '🌹',    label: 'Rose' },
  { id: 'rainbow',     emoji: '🌈',    label: 'Rainbow' },
  { id: 'sun',         emoji: '☀️',    label: 'Sun' },
  { id: 'moon',        emoji: '🌙',    label: 'Moon' },
  { id: 'snowflake',   emoji: '❄️',    label: 'Snowflake' },
] as const

/** Distinguish stored emoji from stored URL (kept future-proof). */
export function isImageUrl(value: string | null | undefined): boolean {
  if (!value) return false
  return value.startsWith('http://') || value.startsWith('https://') || value.startsWith('/')
}

// ─── Knowledge base files ─────────────────────────────────────────────────────
//
// v1: PDF text is extracted client-side via pdfjs-dist (see dialoguePdf.ts).
// We persist only the extracted text + filename — the original PDF is not stored.
// If we ever need re-extraction (better parser, etc.), the admin re-uploads.
//
// Schema cap: 2 PDFs per dialogue, enforced at the API layer (this function).

export const MAX_KB_FILES_PER_DIALOGUE = 2
export const MAX_KB_FILE_BYTES = 7 * 1024 * 1024  // 7 MB

export async function getKbFiles(dialogueId: string): Promise<DialogueKbFile[]> {
  const { data, error } = await supabase
    .from('dialogue_kb_files')
    .select('*')
    .eq('dialogue_id', dialogueId)
    .order('created_at', { ascending: true })

  if (error) throw error
  return (data ?? []) as DialogueKbFile[]
}

export async function addKbFile(opts: {
  dialogueId: string
  createdBy: string
  filename: string
  extractedText: string
}): Promise<DialogueKbFile> {
  // file_url is empty in v1 (no bucket storage). Kept NOT NULL by schema —
  // populate when/if we re-introduce original-file storage.
  const { data, error } = await supabase
    .from('dialogue_kb_files')
    .insert({
      dialogue_id: opts.dialogueId,
      created_by: opts.createdBy,
      filename: opts.filename,
      file_url: '',
      extracted_text: opts.extractedText,
      char_count: opts.extractedText.length,
    })
    .select()
    .single()

  if (error) throw error
  return data as DialogueKbFile
}

export async function deleteKbFile(id: string): Promise<void> {
  const { error } = await supabase
    .from('dialogue_kb_files')
    .delete()
    .eq('id', id)

  if (error) throw error
}

// ─── Scenarios ────────────────────────────────────────────────────────────────

export async function getScenarios(dialogueId: string): Promise<DialogueScenario[]> {
  const { data, error } = await supabase
    .from('dialogue_scenarios')
    .select('*')
    .eq('dialogue_id', dialogueId)
    .order('position', { ascending: true })
    .order('created_at', { ascending: true })

  if (error) throw error
  return (data ?? []) as DialogueScenario[]
}

export async function addScenario(opts: {
  dialogueId: string
  createdBy: string
  prompt: string
  expectedAnswer: string
  source: ScenarioSource
  position: number
}): Promise<DialogueScenario> {
  const { data, error } = await supabase
    .from('dialogue_scenarios')
    .insert({
      dialogue_id: opts.dialogueId,
      created_by: opts.createdBy,
      prompt: opts.prompt,
      expected_answer: opts.expectedAnswer,
      source: opts.source,
      position: opts.position,
    })
    .select()
    .single()

  if (error) throw error
  return data as DialogueScenario
}

export async function updateScenario(
  id: string,
  patch: Partial<Pick<DialogueScenario, 'prompt' | 'expected_answer' | 'position'>>,
): Promise<void> {
  const { error } = await supabase
    .from('dialogue_scenarios')
    .update(patch)
    .eq('id', id)

  if (error) throw error
}

export async function deleteScenario(id: string): Promise<void> {
  const { error } = await supabase
    .from('dialogue_scenarios')
    .delete()
    .eq('id', id)

  if (error) throw error
}

// ─── Customer profiles ──────────────────────────────────────────────────────
//
// N profiles per customer-led dialogue. The play page picks one at random per
// session; the AI plays that profile's `description` verbatim. Replaces the
// legacy single-customer columns on dialogue_configs — those still exist as
// a read-fallback for old rows but should not be written.

/** Cap on profiles per dialogue. 3 is a deliberate constraint: it
 *  forces the author to pick meaningfully distinct personas (e.g.
 *  young/price-sensitive vs middle-aged/premium-minded vs
 *  sceptical/replacement-buyer) instead of building a long buyer
 *  matrix where small variations clutter the allocation. Enforced
 *  at the API level — no DB constraint, so we can raise it later
 *  without a migration if a power user needs more. */
export const MAX_CUSTOMER_PROFILES = 3

export async function getCustomerProfiles(
  dialogueId: string,
): Promise<DialogueCustomerProfile[]> {
  const { data, error } = await supabase
    .from('dialogue_customer_profiles')
    .select('*')
    .eq('dialogue_id', dialogueId)
    .order('position', { ascending: true })
    .order('created_at', { ascending: true })

  if (error) throw error
  return (data ?? []) as DialogueCustomerProfile[]
}

export async function addCustomerProfile(opts: {
  dialogueId: string
  createdBy: string
  name: string
  description: string
  voiceGender: 'F' | 'M'
  iconUrl?: string | null
  lottieData?: object | null
  position: number
}): Promise<DialogueCustomerProfile> {
  const { data, error } = await supabase
    .from('dialogue_customer_profiles')
    .insert({
      dialogue_id: opts.dialogueId,
      created_by: opts.createdBy,
      name: opts.name,
      description: opts.description,
      voice_gender: opts.voiceGender,
      icon_url: opts.iconUrl ?? null,
      lottie_data: opts.lottieData ?? null,
      position: opts.position,
    })
    .select()
    .single()

  if (error) throw error
  return data as DialogueCustomerProfile
}

export async function updateCustomerProfile(
  id: string,
  patch: Partial<Pick<
    DialogueCustomerProfile,
    'name' | 'description' | 'voice_gender' | 'icon_url' | 'lottie_data' | 'position'
  >>,
): Promise<void> {
  const { error } = await supabase
    .from('dialogue_customer_profiles')
    .update({ ...patch, updated_at: new Date().toISOString() })
    .eq('id', id)

  if (error) throw error
}

export async function deleteCustomerProfile(id: string): Promise<void> {
  const { error } = await supabase
    .from('dialogue_customer_profiles')
    .delete()
    .eq('id', id)

  if (error) throw error
}

// ─── Sessions (admin read) ──────────────────────────────────────────────────
//
// Learners create sessions via the SECURITY DEFINER play RPCs (anon path).
// Admins read them back through this client — RLS allows SELECT where
// config_owner = the Clerk userId, so we don't need a server-side RPC for
// the report view. Most-recent first; complete + in-progress mixed (an
// in-progress session has completed_at IS NULL + final_score IS NULL).

export async function getSessions(dialogueId: string): Promise<DialogueSession[]> {
  const { data, error } = await supabase
    .from('dialogue_sessions')
    .select('*')
    .eq('dialogue_id', dialogueId)
    .order('started_at', { ascending: false })

  if (error) throw error
  return (data ?? []) as DialogueSession[]
}
